/**
 * Bottom-sheet "La cuenta de la charla" — detalle de costo de una sesión.
 *
 * No es un modal de fintech: habla el idioma de speak — titular manuscrito
 * (Caveat), números en JetBrains Mono, doodles dibujados a mano (Underline /
 * ShortUnderline), tiles tintados por categoría (púrpura = coach, lima = voz,
 * cyan = tu voz, ámbar = memoria…), y el orbe lima→púrpura para el estado
 * "procesando".
 *
 * Las filas salen del LEDGER (`cost.purposes`: cada débito declara para qué
 * fue) — nunca de "total menos lo que conozco". Una fila residual mentía: las
 * correcciones y la estimación de nivel aparecían como "Memoria".
 *
 * Cuenta una sola historia: qué costó esta charla → cuánto te queda → en qué
 * se fue → (opcional) turno por turno.
 *
 * Settlement-aware: mientras `settlementStatus === 'pending'` (jobs background
 * de distill/memoria todavía pueden debitar), el total puede crecer; repolea
 * cada 3s hasta `'settled'` o timeout (~60s).
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { DebitPurpose } from '@orborix/model-catalog';
import { formatOrbs, formatOrbsCost } from '@orborix/shared-types';
import { ShortUnderline, Underline } from '../../components/doodles/Doodles.js';
import {
  fetchCreditsProjection,
  fetchSessionCost,
  type CreditsProjection,
  type SessionCost,
  type SessionCostPurpose,
} from '../../lib/costApi.js';
import './costSheet.css';
import { useSheetDrag } from '../../hooks/useSheetDrag.js';

export interface SessionCostSheetProps {
  open: boolean;
  sessionId: string | null;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 20; // ~60s a 3s/poll

// Paleta de speak (misma base que ACTIVITY_TYPES): cada categoría de gasto
// tiene su color, tinta el tile del icono (`${color}1f` bg + color).
const CAT = {
  llm: '#a78bfa', // Conversación (coach = púrpura, el color de la charla)
  tts: '#d4f23e', // Voz que escuchaste (lima)
  stt: '#5ce1e6', // Tu voz transcripta (cyan)
  web: '#60a5fa', // Búsqueda web (azul)
  mem: '#fbbf24', // Memoria (ámbar)
  fix: '#fb7185', // Correcciones (rosa)
  analysis: '#34d399', // Análisis de la charla (verde)
  other: '#94a3b8', // Ayudas y resto (gris)
} as const;

interface PurposeRowMeta {
  label: string;
  color: string;
  icon: () => ReactElement;
}

// Exhaustivo a propósito: un propósito nuevo en el ledger no compila hasta
// que alguien decida cómo se le muestra al usuario.
const PURPOSE_ROWS: Readonly<Record<DebitPurpose, PurposeRowMeta>> = {
  conversation: { label: 'Conversación', color: CAT.llm, icon: ChatIcon },
  realtime: { label: 'Charla en vivo', color: CAT.llm, icon: ChatIcon },
  voice: { label: 'Voz que escuchaste', color: CAT.tts, icon: SpeakerIcon },
  transcription: { label: 'Tu voz transcripta', color: CAT.stt, icon: MicIcon },
  web_search: { label: 'Búsqueda web', color: CAT.web, icon: GlobeIcon },
  pronunciation: { label: 'Pronunciación', color: CAT.stt, icon: MicIcon },
  corrections: { label: 'Correcciones', color: CAT.fix, icon: PencilIcon },
  memory: { label: 'Memoria', color: CAT.mem, icon: BrainIcon },
  session_analysis: { label: 'Análisis de la charla', color: CAT.analysis, icon: ChartIcon },
  assists: { label: 'Ayudas (traducir, sugerir, repetir audio)', color: CAT.other, icon: SparkIcon },
  safety: { label: 'Chequeo de seguridad', color: CAT.other, icon: DotsIcon },
  listen_unlock: { label: 'Audioteca', color: CAT.other, icon: SpeakerIcon },
  scenario_generation: { label: 'Escenario a medida', color: CAT.other, icon: SparkIcon },
  theory_chat: { label: 'Charla de teoría', color: CAT.other, icon: ChatIcon },
  fit: { label: 'Fit', color: CAT.other, icon: SparkIcon },
  video_analysis: { label: 'Análisis de video', color: CAT.other, icon: SparkIcon },
  unclassified: { label: 'Sin clasificar', color: CAT.other, icon: DotsIcon },
};

// Débitos viejos que el backfill de 0370 no pudo clasificar.
const LEGACY_ROW: PurposeRowMeta = { label: 'Sin desglose', color: CAT.other, icon: DotsIcon };

/**
 * Una búsqueda web en 0 no es una fila: con DeepSeek la búsqueda no tiene
 * tarifa propia y su costo real (los tokens que inyecta) ya está en
 * "Conversación". Mostrarla en 0 parecía un error. Con Anthropic/Gemini sí
 * cobra por búsqueda y la fila se queda.
 */
function isShownPurpose(p: SessionCostPurpose): boolean {
  return !(p.purpose === 'web_search' && BigInt(p.totalNano) === 0n);
}

function purposeRowMeta(p: SessionCostPurpose): PurposeRowMeta {
  return p.purpose === null ? LEGACY_ROW : PURPOSE_ROWS[p.purpose];
}

export function SessionCostSheet({
  open,
  sessionId,
  onClose,
}: SessionCostSheetProps): ReactElement | null {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  const [cost, setCost] = useState<SessionCost | null>(null);
  const [projection, setProjection] = useState<CreditsProjection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bloquea el scroll del body mientras el sheet está abierto (igual que WordSheet).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !sessionId) return;
    const controller = new AbortController();
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let pollCount = 0;

    setLoading(true);
    setError(null);
    setCost(null);
    setProjection(null);

    const fetchBoth = async (): Promise<void> => {
      try {
        const [c, p] = await Promise.all([
          fetchSessionCost(sessionId, controller.signal),
          fetchCreditsProjection(controller.signal).catch(() => null),
        ]);
        if (cancelled) return;
        setCost(c);
        if (p) setProjection(p);

        // Mientras el settlement está pendiente, el total puede crecer: repolear.
        if (c.settlementStatus === 'pending' && pollCount < MAX_POLLS) {
          pollCount += 1;
          timeoutId = setTimeout(() => void fetchBoth(), POLL_INTERVAL_MS);
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (!cancelled) setError(err instanceof Error ? err.message : 'failed_to_load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchBoth();

    return () => {
      cancelled = true;
      controller.abort();
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [open, sessionId]);

  if (!open) return null;

  return (
    <div
      className="cost-sheet-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cost-sheet-title"
      onClick={onClose}
    >
      <div className="cost-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <div className="cost-sheet-handle sheet-grab" {...grabProps} />
        <div className="cost-sheet-scroll">
          <header className="cost-sheet-head">
            <span className="cost-sheet-kicker" id="cost-sheet-title">
              La cuenta de la charla
            </span>
            <button type="button" className="cost-sheet-x" aria-label="Cerrar" onClick={onClose}>
              <CloseIcon />
            </button>
          </header>

          {loading && !cost ? (
            <div className="cost-sheet-state">
              <span className="cost-sheet-orb" aria-hidden="true" />
              <span className="script cost-sheet-state__msg">sumando los números…</span>
            </div>
          ) : error && !cost ? (
            <div className="cost-sheet-state">
              <span className="script cost-sheet-state__msg">no pudimos cargar el detalle</span>
            </div>
          ) : cost ? (
            <CostDetail cost={cost} projection={projection} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface CostDetailProps {
  cost: SessionCost;
  projection: CreditsProjection | null;
}

function CostDetail({ cost, projection }: CostDetailProps): ReactElement {
  const isPending = cost.settlementStatus === 'pending';

  return (
    <>
      {/* Hero: titular manuscrito + monto mono + subrayado a mano */}
      <div className="cost-sheet-hero">
        {isPending && <span className="cost-sheet-orb cost-sheet-orb--hero" aria-hidden="true" />}
        <span className="script cost-sheet-hero__lead">Esta charla costó</span>
        <span
          className={`cost-sheet-hero__amount${isPending ? ' is-pending' : ''}`}
          aria-label={`Costo total: ${formatOrbs(cost.totalNano)} orbs`}
        >
          {formatOrbs(cost.totalNano)} orbs
        </span>
        <Underline color="#d4f23e" width={132} style={{ marginTop: 2, opacity: 0.9 }} />
        {isPending && <span className="script cost-sheet-hero__note">…todavía sumando</span>}
      </div>

      {projection ? <ProjectionView projection={projection} cost={cost} /> : null}

      {/* Breakdown: pregunta manuscrita + filas con tiles tintados */}
      <div className="cost-sheet-section">
        <span className="script cost-sheet-section__q">¿en qué se fue?</span>
        <ShortUnderline color="#a78bfa" width={92} style={{ margin: '-2px 0 4px 2px' }} />
      </div>
      <div className="cost-sheet-breakdown">
        {cost.purposes.filter(isShownPurpose).map((p) => {
          const meta = purposeRowMeta(p);
          const Icon = meta.icon;
          return (
            <BreakdownRow
              key={p.purpose ?? 'legacy'}
              label={meta.label}
              color={meta.color}
              icon={<Icon />}
              orbs={formatOrbsCost(p.totalNano)}
            />
          );
        })}
        {isPending && (
          // El cierre (memoria, análisis) todavía puede debitar: lo decimos,
          // sin inventar a qué fila va a caer.
          <BreakdownRow
            label="Cierre de la charla"
            color={CAT.mem}
            icon={<BrainIcon />}
            orbs="calculando…"
            pending
          />
        )}
      </div>

      {cost.turns.length > 0 ? (
        <details className="cost-sheet-turns">
          <summary>
            <span>turno por turno</span>
            <span className="cost-sheet-turns__n">{cost.turns.length}</span>
          </summary>
          <div className="cost-sheet-turns-list">
            {cost.turns.map((t) => (
              <div key={t.id} className="cost-sheet-turn-row">
                <span className="cost-sheet-turn-row__i">#{t.index + 1}</span>
                <span>{(t.durationMs / 1000).toFixed(1)}s</span>
                <span className="cost-sheet-turn-row__usd">{formatOrbsCost(t.costNano)}</span>
              </div>
            ))}
          </div>
        </details>
      ) : null}

      <p className="cost-sheet-foot">
        {isPending
          ? 'Procesando tu memoria — el total puede subir un poquito al terminar.'
          : 'Todo incluido: la IA y los servicios de voz.'}
      </p>
    </>
  );
}

interface BreakdownRowProps {
  label: string;
  color: string;
  icon: ReactElement;
  /** Monto en orbs ya formateado (o "calculando…" si pending). */
  orbs: string;
  pending?: boolean;
}

function BreakdownRow({ label, color, icon, orbs, pending = false }: BreakdownRowProps): ReactElement {
  return (
    <div className={`cost-sheet-row${pending ? ' is-pending' : ''}`}>
      <div className="cost-sheet-row-left">
        <span
          className="cost-sheet-row-icon"
          style={{ background: `${color}1f`, color }}
          aria-hidden="true"
        >
          {icon}
        </span>
        <span className="cost-sheet-row-label">{label}</span>
      </div>
      <span className={`cost-sheet-row-amount${pending ? ' is-pending' : ''}`}>{orbs}</span>
    </div>
  );
}

function ProjectionView({
  projection,
  cost,
}: {
  projection: CreditsProjection;
  cost: SessionCost;
}): ReactElement {
  const [showHelp, setShowHelp] = useState(false);
  const balanceNano = BigInt(projection.balanceNano);
  const sessionNano = BigInt(cost.totalNano);
  // Saldo + lo que acabamos de gastar = saldo antes. Para la barra "X% consumido".
  const beforeNano = balanceNano + sessionNano;
  const usedPct = beforeNano > 0n ? Number((sessionNano * 1000n) / beforeNano) / 10 : 0;
  const days = projection.estimatedDaysRemaining;
  const isLow = days !== null && days < 3;

  return (
    <div className="cost-sheet-projection">
      <div className="cost-sheet-projection-top">
        <span className="cost-sheet-projection-bal">
          Te quedan <strong>{formatOrbs(projection.balanceNano)} orbs</strong>
        </span>
        {days !== null ? (
          <span className="cost-sheet-projection-est">
            <span className={`cost-sheet-projection-mins${isLow ? ' is-low' : ''}`}>
              {formatDays(days)}
            </span>
            <button
              type="button"
              className="cost-sheet-projection-help"
              aria-label="Cómo se calcula el tiempo restante"
              aria-expanded={showHelp}
              onClick={() => setShowHelp((v) => !v)}
            >
              <HelpIcon />
            </button>
          </span>
        ) : null}
      </div>
      <div
        className="cost-sheet-projection-bar"
        role="progressbar"
        aria-valuenow={Math.round(usedPct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Porcentaje de saldo usado en esta sesión"
      >
        <div
          className={`cost-sheet-projection-fill${isLow ? ' is-low' : ''}`}
          style={{ width: `${Math.min(Math.max(usedPct, 1.5), 100).toFixed(1)}%` }}
        />
      </div>
      {days !== null ? (
        <span className="script cost-sheet-projection-cta">a tu ritmo habitual</span>
      ) : null}
      {days !== null && showHelp ? (
        <p className="cost-sheet-projection-note">
          Lo calculamos con tu gasto promedio por día de uso de los últimos 30 días.
          Es una estimación a tu ritmo, no un límite — si charlás más, baja más rápido.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Días restantes → string corto: "menos de 1 día", "~N días", o "~N meses"
 * para runways largos (se siente mejor que "~57 días"). Recibe días de
 * calendario al ritmo de gasto real del user — no un supuesto de horas/día.
 * Ver `lib/credits-projection.ts` en el backend.
 */
function formatDays(days: number): string {
  if (!Number.isFinite(days) || days < 0) return '0 días';
  // days viene de un saldo positivo (null se filtra antes); un 0 acá es el
  // truncado de un runway diminuto → "menos de 1 día", no "0 días".
  if (days < 1) return 'menos de 1 día';
  const n = Math.round(days);
  if (n < 60) return `~${n} ${n === 1 ? 'día' : 'días'}`;
  const months = Math.round(days / 30);
  return `~${months} ${months === 1 ? 'mes' : 'meses'}`;
}

/* ── Iconos inline (sin librería, stroke = currentColor) ──────────────── */

function HelpIcon(): ReactElement {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function CloseIcon(): ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 L 6 18 M 6 6 L 18 18" />
    </svg>
  );
}
function ChatIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12 a 9 9 0 0 1 -13 8 L 3 21 L 4 17 A 9 9 0 1 1 21 12 Z" />
    </svg>
  );
}
function SpeakerIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}
function MicIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}
function BrainIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
    </svg>
  );
}
function PencilIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
function ChartIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" />
      <path d="M7 15l4-4 3 3 5-6" />
    </svg>
  );
}
function SparkIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9Z" />
    </svg>
  );
}
function DotsIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="6" y1="12" x2="6.01" y2="12" />
      <line x1="12" y1="12" x2="12.01" y2="12" />
      <line x1="18" y1="12" x2="18.01" y2="12" />
    </svg>
  );
}
function GlobeIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}
